import { useGetDataHook, useSaveDataHook } from '@bymason/api-hooks-base';
import {
  CreateProjectOptions,
  ReadProjectOptions,
  ListProjectsOptions,
  projectsClient,
} from '@bymason/controller-api';
import { useCallback } from 'react';

export {
  CreateProjectOptions,
  ReadProjectOptions,
  ListProjectsOptions,
  CreateProjectResponse,
  ReadProjectResponse,
  ListProjectsResponse,
} from '@bymason/controller-api';

/**
 * React hook that asynchronously gets paginated projects.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useListProjects(clientToken: string) {
  const handleGetData = useCallback(() => {
    return projectsClient.listProjects({}, clientToken);
  }, [clientToken]);

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets all projects.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useListAllProjects(clientToken: string) {
  const getAllProjects = async (clientToken: string) => {
    const opts = {} as ListProjectsOptions;
    const result = await projectsClient.listProjects(opts, clientToken);
    if (result) {
      let nextPage = result.nextPage;
      while (nextPage) {
        opts.after = nextPage;
        const nextResult = await projectsClient.listProjects(opts, clientToken);
        nextPage = nextResult?.nextPage;
        result.data.push(...(nextResult?.data || []));
      }

      return result.data;
    }
    return [];
  };
  const handleGetData = useCallback(() => {
    return getAllProjects(clientToken);
  }, [clientToken]);

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets a project.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useReadProject(clientToken: string) {
  const handleGetData = useCallback(
    (options: ReadProjectOptions) => {
      return projectsClient.readProject(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously creates a project.
 * @returns HookArray
 * @HookArray [0] save method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useCreateProject(clientToken: string) {
  const handleSaveData = useCallback(
    (options: CreateProjectOptions) => {
      return projectsClient.createProject(options, clientToken);
    },
    [clientToken]
  );

  return useSaveDataHook(handleSaveData);
}
